/**
 * VPE Table Section Component
 * Displays the VPE table with pagination and actions
 * Extracted from ManageVPE.tsx for better modularity
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { SquarePen, Trash2 } from 'lucide-react';
import { VPE } from '@/services/features/VPEManagementService';
import PaginationComponent from '@/components/common/PaginationComponent';
import LoaderComponent from '@/components/common/LoaderComponent';
import VPESearchForm from './VPESearchForm';

interface VPETableSectionProps {
  // Data
  paginatedVPEs: VPE[];
  isLoading: boolean;
  deleteButtonLoading: number | null;

  // Pagination
  currentPage: number;
  totalPages: number;
  rowsPerPage: number;
  startIndex: number;
  endIndex: number;
  totalVPEs: number;

  // Search
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  onReload: () => void;

  // Actions
  onEditClick: (vpe: VPE) => void;
  onDeleteClick: (vpe: VPE) => void;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
}

const VPETableSection: React.FC<VPETableSectionProps> = ({
  paginatedVPEs,
  isLoading,
  deleteButtonLoading,
  currentPage,
  totalPages,
  rowsPerPage,
  startIndex,
  endIndex,
  totalVPEs,
  searchValue,
  onSearchChange,
  onSearch,
  onReload,
  onEditClick,
  onDeleteClick,
  onPageChange,
  onRowsPerPageChange,
}) => {
  if (isLoading) {
    return (
      <LoaderComponent
        size="lg"
        text="Loading VPE data..."
        className="h-40"
      />
    );
  }

  return (
    <>
      {/* Search Section */}
      <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-purple-50/50 to-pink-50/50">
        <VPESearchForm
          searchTerm={searchValue}
          onSearchTermChange={onSearchChange}
          onSearch={onSearch}
          onReload={onReload}
          isLoading={isLoading}
        />
        {searchValue && (
          <div className="mt-2 text-sm text-gray-600">
            {totalVPEs === 0 ? 'No results found' : `${totalVPEs} result${totalVPEs === 1 ? '' : 's'} found`}
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-purple-50 to-pink-50">
            <tr>
              <th className="text-left py-6 px-8 font-semibold text-gray-800">Name</th>
              <th className="text-left py-6 px-8 font-semibold text-gray-800">Email</th>
              <th className="text-left py-6 px-8 font-semibold text-gray-800">Allocated Task(s)</th>
              <th className="text-left py-6 px-8 font-semibold text-gray-800">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedVPEs.map((vpe, index) => (
              <tr
                key={vpe.hbox_user_id}
                className="border-b border-gray-100 hover:bg-gradient-to-r hover:from-purple-50/30 hover:to-pink-50/30 transition-all duration-200"
              >
                <td className="py-6 px-8 text-gray-900 font-medium">{vpe.es_name}</td>
                <td className="py-6 px-8 text-gray-700">{vpe.email}</td>
                <td className="py-6 px-8">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${vpe.deferred_task
                    ? 'bg-orange-100 text-orange-800'
                    : 'bg-gray-100 text-gray-800'
                    }`}>
                    {vpe.deferred_task ? 'Deferred Patients' : 'No Task Assigned'}
                  </span>
                </td>
                <td className="py-6 px-8">
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      className="h-8 w-8"
                      onClick={() => onEditClick(vpe)}
                    >
                      <SquarePen className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      className="h-8 w-8 text-red-600 hover:text-red-700"
                      onClick={() => onDeleteClick(vpe)}
                      disabled={deleteButtonLoading === vpe.hbox_user_id}
                    >
                      {deleteButtonLoading === vpe.hbox_user_id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalVPEs > 0 && (
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          recordsPerPage={rowsPerPage}
          totalRecords={totalVPEs}
          startIndex={startIndex}
          endIndex={endIndex}
          onPageChange={onPageChange}
          onRecordsPerPageChange={onRowsPerPageChange}
          className="border-t border-gray-100 bg-gradient-to-r from-purple-50/50 to-pink-50/50"
        />
      )}
    </>
  );
};

export default VPETableSection;